import { useEffect, useMemo, useState } from 'react'
import EmptyState from '../../reusable/EmptyState'
import { Button, ButtonDropdown, Flashbar } from '@amzn/awsui-components-react'
import { calculateStatus } from '../../reusable/AllocationUtils'
import StatusInfoPopover from '../../reusable/StatusInfoPopover'
import AllocationDatePickerWithScroller from '../../reusable/AllocationDatePickerWithScroller'
import { ColDef, GridApi } from 'ag-grid-community'
import { AlertTypes } from '../../../Constant'
import OrgStatusIndicator from './OrgStatusIndicator'
import { DATA_TYPE, FILTER_TYPE } from '../../grid/Constants'
import { addGeneralFilterParams, booleanCellRenderer, dashFallbackRenderer } from '../../grid/Utils'
import GenericAGGridTable from '../../grid/GenericAGGridTable'

const isAllOrgsWeekClose = (orgs) => orgs.every((org) => org.week_closed !== false)
const isAllOrgsWeekOpen = (orgs) => orgs.every((org) => org.week_closed !== true)

const StatusOrgTable = (props) => {
    const {
        selectEndDate,
        onSelectEndDateChange,
        selectedOrgs,
        onSelectedOrgsChange,
        orgs,
        isLoading,
        isTeamLoading,
        onClickCloseReopen,
        isCloseButtonLoading,
        getTeamStatus,
        isDateValid,
        dateErrorText,
        onAlertContentChange,
    } = props

    const [gridApi, setGridApi] = useState<GridApi>()
    const columnDefs = useMemo<ColDef[]>(() => {
        const cols: ColDef[] = [
            { field: 'org_id', headerName: 'Org ID', hide: true },
            { field: 'org_name', headerName: 'Organization', checkboxSelection: true, sort: 'asc' },
            { field: 'description', headerName: 'Description', flex: 2 },
            {
                field: 'entry_status',
                headerName: 'Entry Status',
                valueGetter: ({ data }) =>
                    data.team_entry_status.total_teams
                        ? `${data.team_entry_status.allocation_completed_teams} / ${data.team_entry_status.total_teams}`
                        : 'No team',
                sortable: false,
            },
            {
                field: 'status_indicator',
                headerName: 'Status Indicator',
                valueGetter: ({ data }) =>
                    data.team_entry_status.total_teams ? calculateStatus(data) : 0,
                cellRenderer: ({ data, value }) =>
                    data.team_entry_status.total_teams ? (
                        <OrgStatusIndicator value={value * 100} />
                    ) : (
                        <Flashbar items={[{ content: 'Not Available', type: AlertTypes.INFO }]} />
                    ),
                filter: FILTER_TYPE.NUMBER,
                flex: 2,
            },
            {
                field: 'week_closed',
                headerName: 'Week Closed',
                cellDataType: DATA_TYPE.BOOLEAN,
                cellRenderer: booleanCellRenderer,
            },
        ]
        cols.forEach((col) => {
            addGeneralFilterParams(col)
            col.tooltipField = col.field
            col.cellRenderer ??= dashFallbackRenderer
        })
        return cols
    }, [])

    useEffect(() => {
        if (isLoading) {
            gridApi?.showLoadingOverlay()
        } else {
            gridApi?.setGridOption('rowData', orgs)
        }
    }, [gridApi, orgs, isLoading])

    return (
        <GenericAGGridTable
            setGridApi={setGridApi}
            colDefs={columnDefs}
            rowSelectionType='multiple'
            onSelectionChanged={(event) => {
                onSelectedOrgsChange(event.api.getSelectedRows())
            }}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            noRowsOverlayComponent={() => (
                <>
                    <br /> <br /> <br />
                    <EmptyState
                        title='No orgs'
                        subtitle='No orgs to display.'
                        action={<Button href='/orgs'>Set up Org</Button>}
                    />
                </>
            )}
            includeTableHeader={true}
            primaryButtonPermission={true}
            primaryButton={
                <Button
                    disabled={!selectedOrgs.length}
                    loading={isLoading || isTeamLoading}
                    onClick={() => getTeamStatus(selectEndDate)}
                >
                    Team(s) Preview
                </Button>
            }
            actionDropdownPermission={true}
            actionDropdown={
                <ButtonDropdown
                    variant='primary'
                    loading={isLoading || isCloseButtonLoading}
                    onItemClick={(e) => onClickCloseReopen(e.detail.id)}
                    items={[
                        {
                            text: 'Close',
                            items: [
                                {
                                    text: 'Selected Orgs',
                                    id: 'close_selected_orgs',
                                    disabled:
                                        !selectedOrgs.length || isAllOrgsWeekClose(selectedOrgs),
                                },
                                {
                                    text: 'All Orgs',
                                    id: 'close_all_orgs',
                                    disabled: !orgs.length || isAllOrgsWeekClose(orgs),
                                },
                            ],
                        },
                        {
                            text: 'Reopen',
                            items: [
                                {
                                    text: 'Selected Orgs',
                                    id: 'reopen_selected_orgs',
                                    disabled:
                                        !selectedOrgs.length || isAllOrgsWeekOpen(selectedOrgs),
                                },
                                {
                                    text: 'All Orgs',
                                    id: 'reopen_all_orgs',
                                    disabled: !orgs.length || isAllOrgsWeekOpen(orgs),
                                },
                            ],
                        },
                    ]}
                >
                    Close / Reopen Week
                </ButtonDropdown>
            }
            additionalSelectors={
                <AllocationDatePickerWithScroller
                    disabled={isLoading || isCloseButtonLoading}
                    date={selectEndDate}
                    onDateChange={onSelectEndDateChange}
                    customOnchange={(value) => {
                        onSelectEndDateChange(value)
                        if (selectedOrgs.length) {
                            getTeamStatus(value)
                        }
                        onAlertContentChange('')
                    }}
                    invalid={!isDateValid}
                    expandToViewport={true}
                    label={
                        <StatusInfoPopover
                            title='Week Ending'
                            popoverContent='Select the end date (Friday) for the allocation week in YYYY/MM/DD format.'
                        />
                    }
                    errorText={dateErrorText}
                />
            }
        />
    )
}

export default StatusOrgTable
